import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Box, Typography, Paper } from '@mui/material';
import { hasRoleAccess, UserRole } from '../../config/roleConfig';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string; // For backward compatibility
  requiredRoles?: UserRole[]; // New: support multiple roles
  fallbackPath?: string;
}

/**
 * RoleProtectedRoute component that guards routes requiring specific roles
 * Redirects to login if not authenticated, shows access denied if wrong role
 */
export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  requiredRoles,
  fallbackPath = '/login' 
}) => {
  const { isAuthenticated, getUserRole } = useAuth();
  const location = useLocation();
  const currentUserRole = getUserRole() as UserRole;
  
  console.log('RoleProtectedRoute - isAuthenticated:', isAuthenticated);
  console.log('RoleProtectedRoute - currentUserRole:', currentUserRole);
  console.log('RoleProtectedRoute - requiredRole:', requiredRole);
  console.log('RoleProtectedRoute - requiredRoles:', requiredRoles);

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Determine which roles to check
  let allowedRoles: UserRole[];
  if (requiredRoles) {
    allowedRoles = requiredRoles;
  } else if (requiredRole) {
    // Backward compatibility: convert single role to array
    allowedRoles = [requiredRole as UserRole];
  } else {
    // No roles specified, deny access
    allowedRoles = [];
  }

  // Check role-based access
  const hasAccess = hasRoleAccess(currentUserRole, allowedRoles);

  // Show access denied if user doesn't have required role
  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          p: 3,
        }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Your role: {currentUserRole || 'Unknown'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Required roles: {allowedRoles.join(', ')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Render protected content if authenticated and has required role
  return <>{children}</>;
};